import { AlineTransforms } from '@aline/common';
import { clone } from 'ramda';
import type { Editor, SetSelectionOperation } from 'slate';
import { Operation, Path } from 'slate';

import { HistoryEditor } from './history-editor';

const MAX_UNDOS = 250;

/**
 * Check whether to merge an operation into the previous operation.
 */

function shouldMerge(editor: Editor, op: Operation, prevOp?: Operation): boolean {
  if (editor.operations.length !== 0) return true;

  if (
    prevOp &&
    op.type === 'insert_text' &&
    prevOp.type === 'insert_text' &&
    op.offset === prevOp.offset + prevOp.text.length &&
    Path.equals(op.path, prevOp.path)
  ) {
    return true;
  }

  return !!(
    prevOp &&
    op.type === 'remove_text' &&
    prevOp.type === 'remove_text' &&
    op.offset + op.text.length === prevOp.offset &&
    Path.equals(op.path, prevOp.path)
  );
}

/**
 * Check whether an operation needs to be saved to the history.  We do not store set_selection operations
 * as they are noisy and instead opt to "remember" the current state of the selection right before a batch of operations
 * is pushed to the history.
 */

function shouldSave(op: Operation): boolean {
  return op.type !== 'set_selection';
}

/**
 * Check whether an operation should clear the redos stack.
 */

function shouldClear(op: Operation): boolean {
  return op.type !== 'set_selection';
}

/**
 * The `withHistory` plugins keeps track of the operation history of a Slate
 * editor as operations are applied to it, using undo and redo stacks.
 */

export function withHistory<T extends Editor>(editor: T): T & HistoryEditor {
  const e = editor as T & HistoryEditor;
  const { apply } = e;

  e.history = { redos: [], undos: [] };

  e.redo = (): void => {
    const { history } = e;
    const { redos } = history;

    if (redos.length > 0) {
      const batch = redos[redos.length - 1];

      HistoryEditor.withoutSaving(e, () => {
        AlineTransforms.withoutNormalizing(e, () => {
          for (const op of batch) {
            e.apply(op);
          }
        });
      });

      history.redos.pop();
      history.undos.push(batch);
    }
  };

  e.undo = (): void => {
    const { history } = e;
    const { undos } = history;

    if (undos.length > 0) {
      const batch = undos[undos.length - 1];

      HistoryEditor.withoutSaving(e, () => {
        AlineTransforms.withoutNormalizing(e, () => {
          const inverseOps = batch.map(Operation.inverse).reverse();

          for (const op of inverseOps) {
            e.apply(op);
          }
        });
      });

      history.redos.push(batch);
      history.undos.pop();
    }
  };

  e.apply = (op: Operation): void => {
    const { history } = e;
    const { undos } = history;
    const lastBatch: Operation[] | undefined = undos[undos.length - 1];
    const lastOp: Operation | undefined = lastBatch && lastBatch[lastBatch.length - 1];
    const save = HistoryEditor.isSaving(e) ?? shouldSave(op);
    const merge = HistoryEditor.isMerging(e) ?? shouldMerge(e, op, lastOp);

    if (save) {
      if (lastBatch && merge) {
        lastBatch.push(op);
      } else {
        /**
         * Remember the selection right before a new batch of operations is pushed to the history, so that undo/redo
         * restores the selection correctly as the user sees it.
         */
        if (!e.selection) undos.push([op]);
        else {
          /**
           * Clone the selection to make Immer happy since it freezes objects in dev.
           * Error appears in console if the selection isn't cloned.
           */
          const sel = clone(e.selection);
          const selOp: SetSelectionOperation = {
            newProperties: sel,
            properties: sel,
            type: 'set_selection',
          };
          undos.push([selOp, op]);
        }
      }

      if (undos.length > MAX_UNDOS) undos.splice(0, undos.length - MAX_UNDOS);

      if (shouldClear(op)) history.redos = [];
    }

    apply(op);
  };

  return e;
}
